/**
 * Shared MFC/CGL price-point engine for IPS Panel Replacement and related builders.
 * Exposes window.BWPanelMfcPricePoint { loadVariantMap, computeTotals, roundToNearestNominal }
 */
(function () {
  'use strict';

  function roundToNearestNominal(pounds, profileName) {
    if (typeof window.BDPriceLadder === 'undefined') {
      console.error('BDPriceLadder failed to load; panel-mfc-price-point rounding is unavailable.');
      return null;
    }
    if (!profileName) {
      throw new Error('BWPanelMfcPricePoint.roundToNearestNominal: profile is required');
    }
    return window.BDPriceLadder.roundToRung(pounds, window.BDPriceLadder.getProfile(profileName));
  }

  function cutPerimeterMm(len, wid) {
    return 2 * (Math.max(0, len) + Math.max(0, wid));
  }

  var GRAIN_LOCKED_CODES = new Set([
    '0260 FH', '4029 FH', '0607 FH', '0419 FH', '0361 FH', '0940 FH', '0551 FH',
    'B100', 'B101', 'C102', 'C183', 'C179', 'C212', 'C202', 'F086', 'N056', 'N026', 'T105',
    'S63066', 'R50003', 'R20039', 'R20021', 'R20098', 'R34033'
  ]);

  function shelfPack(rects, sheetW, sheetH, allowRotate) {
    if (rects.length === 0) return { sheets: 0, wastePct: 0, winner: '' };

    function packWithSort(sorted) {
      var sheets = 1, cursorX = 0, cursorY = 0, rowHeight = 0, wasteArea = 0;
      for (var ri = 0; ri < sorted.length; ri++) {
        var r = sorted[ri];
        var rw = r.w, rh = r.h;
        var tmp;
        if (allowRotate) {
          var fits1 = (cursorX + rw <= sheetW) && (Math.max(rowHeight, rh) <= sheetH - cursorY);
          var fits2 = (cursorX + rh <= sheetW) && (Math.max(rowHeight, rw) <= sheetH - cursorY);
          if (!fits1 && fits2) { rw = r.h; rh = r.w; }
          else if (fits1 && fits2) {
            if (rowHeight === 0) { if (rh < rw) { tmp = rw; rw = rh; rh = tmp; } }
            else {
              var diffKeep = Math.abs(rowHeight - rh), diffSwap = Math.abs(rowHeight - rw);
              if (diffSwap < diffKeep) { tmp = rw; rw = rh; rh = tmp; }
            }
          }
        }
        if (cursorX + rw <= sheetW && cursorY + Math.max(rowHeight, rh) <= sheetH) {
          cursorX += rw;
          rowHeight = Math.max(rowHeight, rh);
        } else if (rw <= sheetW && cursorY + rowHeight + rh <= sheetH) {
          cursorY += rowHeight;
          if (allowRotate) {
            var fitsKeep = rw <= sheetW;
            var fitsSwap = rh <= sheetW;
            if (fitsKeep && fitsSwap) { if (rw < rh) { tmp = rw; rw = rh; rh = tmp; } }
            else if (!fitsKeep && fitsSwap) { tmp = rw; rw = rh; rh = tmp; }
          }
          cursorX = rw;
          rowHeight = rh;
        } else if (allowRotate && rh <= sheetW && cursorY + rowHeight + rw <= sheetH) {
          cursorY += rowHeight;
          cursorX = rh;
          rowHeight = rw;
        } else if (allowRotate) {
          wasteArea += (sheetW * sheetH) - (cursorY + rowHeight) * sheetW;
          sheets++;
          cursorY = 0;
          var fk = rw <= sheetW && rh <= sheetH;
          var fs = rh <= sheetW && rw <= sheetH;
          if (fk && fs) { if (rh < rw) { tmp = rw; rw = rh; rh = tmp; } }
          else if (!fk && fs) { tmp = rw; rw = rh; rh = tmp; }
          cursorX = rw;
          rowHeight = rh;
        } else {
          wasteArea += (sheetW * sheetH) - (cursorY + rowHeight) * sheetW;
          sheets++;
          cursorX = rw;
          cursorY = 0;
          rowHeight = rh;
        }
      }
      wasteArea += (sheetW * sheetH) - (cursorY + rowHeight) * sheetW;
      var totalSheetArea = sheets * sheetW * sheetH;
      return { sheets: sheets, wastePct: totalSheetArea > 0 ? (wasteArea / totalSheetArea) * 100 : 0 };
    }

    var sortStrategies = [
      { name: 'A', fn: function (a, b) { return Math.max(b.w, b.h) - Math.max(a.w, a.h); } },
      { name: 'B', fn: function (a, b) { return (b.w * b.h) - (a.w * a.h); } },
      { name: 'C', fn: function (a, b) { return b.w - a.w; } },
      { name: 'D', fn: function (a, b) { return b.h - a.h; } },
      { name: 'E', fn: function (a, b) { return (2 * (b.w + b.h)) - (2 * (a.w + a.h)); } },
      { name: 'F', fn: function (a, b) {
        return (Math.max(b.w, b.h) / Math.min(b.w, b.h)) - (Math.max(a.w, a.h) / Math.min(a.w, a.h));
      } },
      { name: 'G', fn: function (a, b) { return Math.min(b.w, b.h) - Math.min(a.w, a.h); } },
      { name: 'H', fn: function (a, b) { return (a.w * a.h) - (b.w * b.h); } }
    ];

    var best = { sheets: Infinity, wastePct: 100, winner: 'A' };
    for (var si = 0; si < sortStrategies.length; si++) {
      var strat = sortStrategies[si];
      var sorted = rects.slice().sort(strat.fn);
      var result = packWithSort(sorted);
      if (result.sheets < best.sheets || (result.sheets === best.sheets && result.wastePct < best.wastePct)) {
        best = { sheets: result.sheets, wastePct: result.wastePct, winner: strat.name };
      }
    }
    return best;
  }

  function edgeMmPerPanel(len, wid, edgeCode) {
    var L = Math.max(0, len), W = Math.max(0, wid);
    switch (String(edgeCode)) {
      case '0': return 0;
      case '1': return L;
      case '2': return 2 * L;
      case '3': return W;
      case '6': return 2 * W;
      case '7': return L + 2 * W;
      case '5': return 2 * L + W;
      case '4':
      default: return 2 * L + 2 * W;
    }
  }

  function getBoardDims(manuStr, boardSizes, defaultBoard) {
    var m = (manuStr || '').trim().toLowerCase();
    if (!m) return defaultBoard;
    if (boardSizes[m]) return boardSizes[m];
    var keys = Object.keys(boardSizes);
    for (var i = 0; i < keys.length; i++) {
      if (m.indexOf(keys[i]) !== -1) return boardSizes[keys[i]];
    }
    return defaultBoard;
  }

  function computeTotals(opts) {
    var colour = opts.colour || {};
    var rowDataList = opts.rowDataList || [];
    var boardSizes = opts.boardSizes || {};
    var boardPrices = opts.boardPrices || {};
    var cutPricePerM = Number(opts.cutPricePerM || 0);
    var edgePricePerM = Number(opts.edgePricePerM || 0);
    var deliveryBase = Number(opts.deliveryBase || 0);
    var deliveryPerSheet = Number(opts.deliveryPerSheet || 0);
    var includeDeliveryBase = opts.includeDeliveryBase !== false;
    var variantMap = opts.variantMap || {};
    var extraCostPenceBeforeMarkup = Number(opts.extraCostPenceBeforeMarkup || 0);
    if (!opts.mapPrefix) {
      throw new Error('BWPanelMfcPricePoint.computeTotals: mapPrefix is required');
    }
    var mapPrefix = String(opts.mapPrefix).toUpperCase();
    /* Per-panel handling: load, label, QC, wrap. Scales with panel COUNT, not area. */
    var handlingPencePerPanel = Number(opts.handlingPencePerPanel || 0);
    /* Per-sheet amount added once, after the sheet price. Laminate bonding on MR MDF. */
    var postMarkupPencePerSheet = Number(opts.postMarkupPencePerSheet || 0);

    var DEFAULT_BOARD = { l: 2700, w: 1970 };
    var dims = getBoardDims(colour.manu, boardSizes, DEFAULT_BOARD);
    var usableArea = dims.l * dims.w;

    var totalArea = 0, totalPanels = 0, totalCutMm = 0, totalEdgeMm = 0, firstErr = '';

    for (var ri = 0; ri < rowDataList.length; ri++) {
      var d = rowDataList[ri];
      var l = Number(d.l) || 0, w = Number(d.w) || 0;
      var q = Math.max(1, Number(d.q) || 1);
      var e = String(d.e != null ? d.e : '4');
      if (l <= 0 || w <= 0) continue;

      var fitsNormal = l <= dims.l && w <= dims.w;
      var fitsRotated = w <= dims.l && l <= dims.w;
      if (!fitsNormal && !fitsRotated) {
        var manuLabel = colour.manu || 'selected brand';
        firstErr = firstErr || ('Panel ' + l + '\u00d7' + w + 'mm exceeds the usable sheet size for ' + manuLabel +
          ' (' + dims.l + '\u00d7' + dims.w + 'mm). Please reduce your panel dimensions.');
        continue;
      }

      totalArea += (l * w) * q;
      totalPanels += q;
      totalCutMm += cutPerimeterMm(l, w) * q;
      totalEdgeMm += edgeMmPerPanel(l, w, e) * q;
    }

    var sheetsArea = (totalArea > 0 && usableArea > 0) ? Math.ceil(totalArea / usableArea) : 0;
    var allowRotate = !GRAIN_LOCKED_CODES.has(colour.code || '');
    var sheetsShelf = 0;
    var packingResult = { sheets: 0, wastePct: 0, winner: '' };
    var expandedRects = [];

    if (totalPanels > 0) {
      for (var ej = 0; ej < rowDataList.length; ej++) {
        var rd = rowDataList[ej];
        var L = Number(rd.l) || 0, W = Number(rd.w) || 0;
        var Q = Math.max(1, Number(rd.q) || 1);
        if (L <= 0 || W <= 0) continue;
        var fn = L <= dims.l && W <= dims.w;
        var fr = W <= dims.l && L <= dims.w;
        if (!fn && !fr) continue;
        for (var k = 0; k < Q; k++) expandedRects.push({ w: L, h: W });
      }
      if (expandedRects.length <= 20000) {
        packingResult = shelfPack(expandedRects, dims.l, dims.w, allowRotate);
        sheetsShelf = packingResult.sheets;
      }
    }

    var sheets = Math.max(sheetsShelf, sheetsArea);
    var totalCutMetres = totalCutMm / 1000;
    var totalEdgeMetres = totalEdgeMm / 1000;
    var hasPanels = totalPanels > 0 && sheets > 0 && !firstErr;

    if (!hasPanels) {
      return {
        totals: {
          sheets: 0, sheetsShelf: 0, sheetsArea: 0, panels: 0,
          totalCutMetres: 0, totalEdgeMetres: 0,
          exactTotalPence: 0, pricePoint: 0, totalCost: 0,
          brandSheetCostPence: 0, packingWinner: '', packingWastePct: 0,
          packingRectCount: 0, allowRotate: allowRotate, hasPanels: false, error: firstErr
        },
        resolvedVariant: null
      };
    }

    var brand = (colour.manu || '').trim().toLowerCase();
    var brandSheetPrice = boardPrices[brand] || 0;

    var handlingPence = totalPanels * handlingPencePerPanel;

    var costPence =
      (sheets * brandSheetPrice) +
      Math.round(totalCutMetres * cutPricePerM) +
      Math.round(totalEdgeMetres * edgePricePerM) +
      (includeDeliveryBase ? deliveryBase : 0) +
      (sheets * deliveryPerSheet) +
      handlingPence +
      extraCostPenceBeforeMarkup;

    var exactTotalPence = costPence + (sheets * postMarkupPencePerSheet);
    var totalPounds = exactTotalPence / 100;
    var roundedPounds = roundToNearestNominal(totalPounds, opts.profile);
    var profile = window.BDPriceLadder.getProfile(opts.profile);
    var cap = profile.cap;
    var floor = profile.floor;
    var clampedPounds = Math.max(floor, Math.min(cap, roundedPounds));

    var key = 'BD_' + mapPrefix + '_' + clampedPounds + '_CP';
    var mapLoaded = Object.keys(variantMap).length > 0;
    var varId = variantMap[key];
    var resolvedVariant = null;

    if (roundedPounds > cap) {
      resolvedVariant = null;
    } else if (!varId) {
      resolvedVariant = null;
    } else {
      resolvedVariant = { id: varId, price: clampedPounds * 100, configKey: key };
    }

    var totalCost = clampedPounds * 100;
    var panelsValid = totalPanels > 0 && sheets > 0 && !firstErr && roundedPounds <= cap;

    return {
      totals: {
        sheets: sheets,
        sheetsShelf: sheetsShelf,
        sheetsArea: sheetsArea,
        panels: totalPanels,
        totalCutMetres: totalCutMetres,
        totalEdgeMetres: totalEdgeMetres,
        exactTotalPence: exactTotalPence,
        pricePoint: clampedPounds,
        totalCost: totalCost,
        brandSheetCostPence: brandSheetPrice,
        handlingPence: handlingPence,
        packingWinner: packingResult.winner,
        packingWastePct: packingResult.wastePct,
        packingRectCount: expandedRects.length,
        allowRotate: allowRotate,
        hasPanels: panelsValid,
        error: firstErr
      },
      resolvedVariant: resolvedVariant
    };
  }

  async function loadVariantMap(url) {
    if (!url) return {};
    var res = await fetch(url);
    if (!res.ok) throw new Error('Variant map fetch failed: ' + res.status);
    return await res.json();
  }

  window.BWPanelMfcPricePoint = {
    loadVariantMap: loadVariantMap,
    computeTotals: computeTotals,
    roundToNearestNominal: roundToNearestNominal
  };
})();
